import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { CMS_CONTENT_TYPE_SEGMENTS } from "./cmsContentTypes";
import { isIpv4Address } from "./utils/ipAddress";
import { PageViewTracker } from "./components/PageViewTracker";
import { useLanguages } from "./languages/useLanguages";
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { WebMcpBridge } from "./webmcp/WebMcpBridge";

const queryClient = new QueryClient();

// Home is the overwhelming majority of visits, so it stays in the main bundle; every other route
// (including its own transitive deps like ResultView/Leaflet on CmsContentPage's neighbors) is
// fetched on demand instead of shipping on every home-page load — this is what "netriskscan.com
// index-*.js ships ~83 KiB of unused JS on the home page" in Lighthouse was pointing at.
const AdminApp = lazy(() => import("./admin/AdminApp").then((module) => ({ default: module.AdminApp })));
const DeveloperApp = lazy(() => import("./developer/DeveloperApp").then((module) => ({ default: module.DeveloperApp })));
const PricingPage = lazy(() => import("./developer/pages/PricingPage").then((module) => ({ default: module.PricingPage })));
const CmsContentPage = lazy(() => import("./pages/CmsContentPage").then((module) => ({ default: module.CmsContentPage })));
const BlogPage = lazy(() => import("./pages/BlogPage").then((module) => ({ default: module.BlogPage })));
const StaticContentPage = lazy(() => import("./pages/StaticContentPage").then((module) => ({ default: module.StaticContentPage })));
const SharedResultPage = lazy(() => import("./pages/SharedResultPage").then((module) => ({ default: module.SharedResultPage })));
const ClaudeAccessPage = lazy(() => import("./pages/ClaudeAccessPage").then((module) => ({ default: module.ClaudeAccessPage })));
const WebrtcLeakTestPage = lazy(() => import("./pages/WebrtcLeakTestPage").then((module) => ({ default: module.WebrtcLeakTestPage })));
const BrowserFingerprintTestPage = lazy(() =>
  import("./pages/BrowserFingerprintTestPage").then((module) => ({ default: module.BrowserFingerprintTestPage })),
);
const DomainSecurityCheckPage = lazy(() =>
  import("./pages/DomainSecurityCheckPage").then((module) => ({ default: module.DomainSecurityCheckPage })),
);
const Ipv6LeakTestPage = lazy(() => import("./pages/Ipv6LeakTestPage").then((module) => ({ default: module.Ipv6LeakTestPage })));
const DnsLeakTestPage = lazy(() => import("./pages/DnsLeakTestPage").then((module) => ({ default: module.DnsLeakTestPage })));
const EnvironmentCheckPage = lazy(() => import("./pages/EnvironmentCheckPage").then((module) => ({ default: module.EnvironmentCheckPage })));
const ComparePage = lazy(() => import("./pages/ComparePage").then((module) => ({ default: module.ComparePage })));
const ReportPage = lazy(() => import("./pages/ReportPage").then((module) => ({ default: module.ReportPage })));
const ServerCheckPage = lazy(() => import("./pages/ServerCheckPage").then((module) => ({ default: module.ServerCheckPage })));
const NetworkScanReportsPage = lazy(() => import("./pages/NetworkScanReportsPage").then((module) => ({ default: module.NetworkScanReportsPage })));
const NetworkScanHistoryPage = lazy(() => import("./pages/NetworkScanHistoryPage").then((module) => ({ default: module.NetworkScanHistoryPage })));
const NetworkSpeedTestPage = lazy(() => import("./pages/NetworkSpeedTestPage").then((module) => ({ default: module.NetworkSpeedTestPage })));

// Home pages use directory roots (/ and /{lang}/); the Worker maps translated roots to their
// physical /{lang}/index.html build snapshots.
// CMS content reuses the same convention: /{contentType}/{slug}.html for the default language,
// /{lang}/{contentType}/{slug}.html otherwise (see NetRiskScan.Application.Common.CmsUrlHelper).
//
// Every "/:lang/..." route below matches ANY string in that segment, not just real language codes —
// react-router has no built-in way to constrain a param to a dynamic list. Left unguarded, a typo'd
// or guessed URL like "/xyzzy/" rendered the wrapped page with lang="xyzzy": a nonsense value that
// (a) fed straight into Intl.*(lang) calls downstream and threw, crashing to a blank page, and
// (b) was otherwise a fully indexable 200 OK duplicate of that page's default-language content at an
// unbounded number of fake URLs. This gate renders the 404 page (noindex) for any segment that isn't
// in the real, admin-managed language list once that list has loaded; before it loads, children
// render optimistically so the overwhelmingly common case (a real language code) never waits on it.
//
// The unprefixed route of every localized pair is wrapped too, where it is a pass-through (no :lang).
// That keeps "/x.html" and "/:lang/x.html" the same element tree, so React Router re-renders the mounted
// page on a language switch instead of remounting it — the same rule as HomeRoute below. A remount threw
// away whatever the page was showing: Compare's loaded/edited slots, a tool's finished run (re-running
// it), on every switch between the default language and any other.
function LangGate({ children }: { children: ReactNode }) {
  const { lang } = useParams();
  const { data: languages } = useLanguages();
  const isKnownLanguage = !languages || !lang || languages.some((language) => language.urlCode.toLowerCase() === lang.toLowerCase());
  return isKnownLanguage ? <>{children}</> : <NotFoundPage />;
}

// spec §二十一: `https://www.netriskscan.com/1.1.1.1` used to look up that address directly by rendering
// HomePage off the path segment. The canonical mechanism is now the `?ip=` query parameter instead
// (`https://www.netriskscan.com/?ip=1.1.1.1`) — HomePage reads it itself via useSearchParams, from any
// route that renders it, rather than requiring a dedicated single-segment path and a prop threaded down
// from here. `/{ipv4}` is kept as a compatibility redirect so links and bookmarks made before this change
// keep working.
//
// This still has to share one route with the language prefix rather than sitting beside it: `/:lang/` and
// a hypothetical `/:ipv4` are the same shape to react-router, which ranks routes by specificity rather
// than by declaration order, so two single-segment dynamic routes would match ambiguously. One component
// that inspects the segment is unambiguous by construction, and it keeps the precedence rule explicit and
// testable instead of implicit in route ordering.
//
// Precedence is IPv4 first, then language. The two sets cannot collide — no language code is four
// dot-separated numeric octets — and every *static* one-segment route (/about.html, /blog.html, /admin,
// /login, ...) is declared above and wins over any dynamic segment regardless. An invalid address like
// /999.1.1.1 fails isIpv4Address, falls through to the language branch, fails that too, and renders the
// 404 page: no provider is ever queried for it (spec §二十一).
//
// Every home route (/, /index.html, /:lang, /:lang/index.html) renders this same component, with only
// the legacy-IP prop differing. React Router reuses the rendered element when two matches produce the
// same component type, so a language switch (/ → /zh-cn/, /zh-cn/ → /de/) re-renders the one HomePage
// instead of unmounting it and mounting a fresh one — the locale is display state, and a remount
// would throw away the result on screen and re-run its RiskQuery.
function HomeRoute({ legacyIpSegment = false }: { legacyIpSegment?: boolean }) {
  // Named `lang` rather than `segment` because both branches below re-read it from useParams: LangGate
  // validates it against the admin-managed language list, and the redirect below reuses it as the ip
  // value. Renaming the param here would silently break both.
  const { lang } = useParams();
  const { search } = useLocation();

  if (legacyIpSegment && lang && isIpv4Address(lang)) {
    // Keep whatever else was on the query string (UTM tags in particular): a redirect that drops it
    // would make a tagged campaign link look untagged. `ip` is set last so the path segment wins.
    const params = new URLSearchParams(search);
    params.set("ip", lang);
    return <Navigate to={`/?${params.toString()}`} replace />;
  }

  return <LangGate><HomePage /></LangGate>;
}

// The route table here and src/routes/spaRoutes.ts must list the same paths: the edge Worker serves the
// app shell only for paths in that table and answers 404 for everything else (spaRoutes.test.ts
// enforces the match). Exported for that test and for the routing tests.
export function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/index.html" element={<HomeRoute />} />
        <Route path="/share/:id" element={<SharedResultPage />} />
        <Route path="/r/:id" element={<ReportPage />} />
        <Route path="/environment-check.html" element={<LangGate><EnvironmentCheckPage /></LangGate>} />
        <Route path="/:lang/environment-check.html" element={<LangGate><EnvironmentCheckPage /></LangGate>} />
        <Route path="/server-check.html" element={<LangGate><ServerCheckPage /></LangGate>} />
        <Route path="/:lang/server-check.html" element={<LangGate><ServerCheckPage /></LangGate>} />
        <Route path="/reports.html" element={<LangGate><NetworkScanReportsPage /></LangGate>} />
        <Route path="/:lang/reports.html" element={<LangGate><NetworkScanReportsPage /></LangGate>} />
        {/* Private per-server scan timeline, keyed by the token in the URL fragment. Reached from the
            Agent's own terminal output and marked noindex, so unlike the other pages it publishes no
            canonical/hreflang tags — the :lang variant exists only to keep the copy translated. */}
        <Route path="/history.html" element={<LangGate><NetworkScanHistoryPage /></LangGate>} />
        <Route path="/:lang/history.html" element={<LangGate><NetworkScanHistoryPage /></LangGate>} />
        <Route path="/compare.html" element={<LangGate><ComparePage /></LangGate>} />
        <Route path="/:lang/compare.html" element={<LangGate><ComparePage /></LangGate>} />
        <Route path="/claude-access.html" element={<LangGate><ClaudeAccessPage /></LangGate>} />
        <Route path="/:lang/claude-access.html" element={<LangGate><ClaudeAccessPage /></LangGate>} />
        <Route path="/webrtc-leak-test.html" element={<LangGate><WebrtcLeakTestPage /></LangGate>} />
        <Route path="/:lang/webrtc-leak-test.html" element={<LangGate><WebrtcLeakTestPage /></LangGate>} />
        <Route path="/browser-fingerprint-test.html" element={<LangGate><BrowserFingerprintTestPage /></LangGate>} />
        <Route path="/:lang/browser-fingerprint-test.html" element={<LangGate><BrowserFingerprintTestPage /></LangGate>} />
        <Route path="/domain-security-check.html" element={<LangGate><DomainSecurityCheckPage /></LangGate>} />
        <Route path="/:lang/domain-security-check.html" element={<LangGate><DomainSecurityCheckPage /></LangGate>} />
        <Route path="/ipv6-leak-test.html" element={<LangGate><Ipv6LeakTestPage /></LangGate>} />
        <Route path="/:lang/ipv6-leak-test.html" element={<LangGate><Ipv6LeakTestPage /></LangGate>} />
        <Route path="/dns-leak-test.html" element={<LangGate><DnsLeakTestPage /></LangGate>} />
        <Route path="/:lang/dns-leak-test.html" element={<LangGate><DnsLeakTestPage /></LangGate>} />
        <Route path="/network-speed-test.html" element={<LangGate><NetworkSpeedTestPage /></LangGate>} />
        <Route path="/:lang/network-speed-test.html" element={<LangGate><NetworkSpeedTestPage /></LangGate>} />
        <Route path="/blog.html" element={<LangGate><BlogPage /></LangGate>} />
        <Route path="/:lang/blog.html" element={<LangGate><BlogPage /></LangGate>} />
        <Route path="/blog/category/:categorySlug" element={<LangGate><BlogPage categoryPage /></LangGate>} />
        <Route path="/:lang/blog/category/:categorySlug" element={<LangGate><BlogPage categoryPage /></LangGate>} />
        {/* /tools.html is retired (edgeRetiredPages.ts -> /, 301 at the edge) — no SPA route
            for it, so a stale client-side navigation falls through to NotFoundPage rather than
            resurrecting the old directory page. */}
        <Route path="/about.html" element={<LangGate><StaticContentPage page="about" /></LangGate>} />
        <Route path="/:lang/about.html" element={<LangGate><StaticContentPage page="about" /></LangGate>} />
        <Route path="/contact.html" element={<LangGate><StaticContentPage page="contact" /></LangGate>} />
        <Route path="/:lang/contact.html" element={<LangGate><StaticContentPage page="contact" /></LangGate>} />
        <Route path="/privacy.html" element={<LangGate><StaticContentPage page="privacy" /></LangGate>} />
        <Route path="/:lang/privacy.html" element={<LangGate><StaticContentPage page="privacy" /></LangGate>} />
        <Route path="/terms.html" element={<LangGate><StaticContentPage page="terms" /></LangGate>} />
        <Route path="/:lang/terms.html" element={<LangGate><StaticContentPage page="terms" /></LangGate>} />
        <Route path="/refund-policy.html" element={<LangGate><StaticContentPage page="refund-policy" /></LangGate>} />
        <Route path="/:lang/refund-policy.html" element={<LangGate><StaticContentPage page="refund-policy" /></LangGate>} />
        <Route path="/disclaimer.html" element={<LangGate><StaticContentPage page="disclaimer" /></LangGate>} />
        <Route path="/:lang/disclaimer.html" element={<LangGate><StaticContentPage page="disclaimer" /></LangGate>} />
        <Route path="/editorial-policy.html" element={<LangGate><StaticContentPage page="editorial-policy" /></LangGate>} />
        <Route path="/:lang/editorial-policy.html" element={<LangGate><StaticContentPage page="editorial-policy" /></LangGate>} />
        <Route path="/author/netriskscan-editorial-team.html" element={<LangGate><StaticContentPage page="author" /></LangGate>} />
        <Route path="/:lang/author/netriskscan-editorial-team.html" element={<LangGate><StaticContentPage page="author" /></LangGate>} />
        <Route path="/pricing.html" element={<LangGate><PricingPage /></LangGate>} />
        <Route path="/:lang/pricing.html" element={<LangGate><PricingPage /></LangGate>} />
        {/* /developer/* is an app area, not an indexed page — every page inside noindex,nofollow via
            <NoIndex/> (see developer/components/NoIndex.tsx). It still gets a "/:lang/developer/*"
            variant so DeveloperI18nProvider (see developer/i18n/DeveloperI18nProvider.tsx) can
            resolve a translated DeveloperStrings dictionary from the segment, but — unlike the SEO
            pages above — it's deliberately NOT wrapped in LangGate: an unrecognized :lang here should
            gracefully fall back to English content, not 404, since this is an authenticated app area
            rather than an indexed surface that needs a hard validity gate. */}
        <Route path="/developer/*" element={<DeveloperApp />} />
        <Route path="/:lang/developer/*" element={<DeveloperApp />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/login" element={<Navigate to="/admin/login" replace />} />
        <Route path="/providers" element={<Navigate to="/admin/providers" replace />} />
        <Route path="/api-clients" element={<Navigate to="/admin/api-clients" replace />} />
        <Route path="/query-logs" element={<Navigate to="/admin/query-logs" replace />} />
        <Route path="/languages" element={<Navigate to="/admin/languages" replace />} />
        <Route path="/:lang" element={<HomeRoute legacyIpSegment />} />
        <Route path="/:lang/index.html" element={<HomeRoute />} />
        {CMS_CONTENT_TYPE_SEGMENTS.map((contentType) => (
          <Route key={contentType} path={`/${contentType}/:slug`} element={<LangGate><CmsContentPage contentType={contentType} /></LangGate>} />
        ))}
        {CMS_CONTENT_TYPE_SEGMENTS.map((contentType) => (
          <Route
            key={`localized-${contentType}`}
            path={`/:lang/${contentType}/:slug`}
            element={<LangGate><CmsContentPage contentType={contentType} /></LangGate>}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <WebMcpBridge />
        <PageViewTracker />
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
